import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { areIntervalsOverlapping, startOfTomorrow } from 'date-fns';

import {
	Button,
	DatepickerRange,
	Inform,
	Modal,
	Select,
	SpinnerOverlay,
	TextArea } from '@components/common';

import { useQueryAlreadyRentedAssetsDatesPicked } from '@hooks/assets/useQueryAlreadyRentedAssetsDatesPicked';
import { useQueryAssetsListDropdown } from '@hooks/assets/useQueryAssetsListDropdown';
import { useQueryUser } from '@hooks/useQueryUser';

import { getPickedDates } from '@api/assetsRent';

import { isEmpty } from '@utils/common';
import { generateSelectOptions } from '@utils/generateSelectOptions/generateSelectOptions';
import { endOfDayInUTC, getDateWithoutOffset, startOfDayInUTC } from '@utils/kpi-utils/date';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAssetRentRequestCardItem, IRentedOutAssetDate, TRangeDate } from '@interfaces/rent/assetRent';
import { IAddItemToExistingAssetRentRequestDTO } from '@interfaces/rent/dto/assetRentDTO';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { assetIdPlaceholder, cancelBtnText, defaultConfirmBtnText } from './constants';

import { schema } from './schema';

import styles from './AddNewAssetToRequest.module.scss';

const intervalErrorText = 'Interval includes forbidden days';

interface IAddNewAssetToRequest {
	addNewAssetToRequest: (params: IAddItemToExistingAssetRentRequestDTO) =>
		SubmitHandler<IAddItemToExistingAssetRentRequestDTO> | void;
	assetItems?: Array<IAssetRentRequestCardItem>;
	closeModal: () => void;
	confirmBtnText?: string;
	requestDbId?: string;
	selectedAssetId?: TSelectOption;
	setSelectedAssetId?: (assetId?: string) => void;
}

export interface IAddAssetFormFieldsType {
	assetDbId?: TSelectOption;
	from?: number;
	to?: number;
	comment?: string;
}

function AddNewAssetToRequest({
	addNewAssetToRequest,
	closeModal,
	confirmBtnText = defaultConfirmBtnText,
	requestDbId,
	selectedAssetId,
	setSelectedAssetId,
}: IAddNewAssetToRequest) {
	const [isOverlapDates, setIsOverlapDates] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		watch,
		clearErrors,
		setError,
		getValues,
		formState: { errors },
		register,
	} = useForm<IAddAssetFormFieldsType>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		reValidateMode: ERevalidationMode.onChange,
	});

	useEffect(() => {
		if (selectedAssetId) {
			setValue('assetDbId', selectedAssetId);
		}
	}, [selectedAssetId, setValue]);

	const assetId = getValues('assetDbId');

	useEffect(() => {
		if (setSelectedAssetId && assetId) {
			setSelectedAssetId(assetId?.value as string);
		}
	}, [assetId, setSelectedAssetId]);

	const onSubmit = async (values: FieldValues): Promise<void> => {
		const assetId = selectedAssetId?.id || values.assetDbId?.value;

		if (assetId) {
			const { dates } = await getPickedDates(assetId as string, requestDbId as string);

			const isOverlapChosenInterval = dates?.some((item: IRentedOutAssetDate) => {
				return item?.ranges.some((range: TRangeDate) => {
					return areIntervalsOverlapping(
						{
							start: range.from,
							end: range.to,
						},
						{
							start: startOfDayInUTC(new Date(values.from)),
							end: endOfDayInUTC(new Date(values.to)),
						},
					);
				});
			});

			if (isOverlapChosenInterval) {
				return setIsOverlapDates(true);
			}
		}

		addNewAssetToRequest({
			assetDbId: values.assetDbId.value,
			startDate: values?.from ? startOfDayInUTC(new Date(values.from)) : null,
			endDate: values.to ? endOfDayInUTC(new Date(values.to)) : null,
			requestDbId,
			comment: values.comment,
		} as IAddItemToExistingAssetRentRequestDTO);
	};

	const { organisationId } = useQueryUser();

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const { assets: assetsList, isLoadingAssets } = useQueryAssetsListDropdown({
		isOwnOnly: true,
		ownerId: organisationId,
		page: 1,
		pageSize,
		sortDirection: true,
		sortFieldName: 'assetId',
		searchValue: '',
	});

	const asset = selectedAssetId || assetsList.find(asset => asset.id === watch('assetDbId')?.value);

	const { dates: disabledDatesRanges, isLoadingDates } = useQueryAlreadyRentedAssetsDatesPicked(
		asset?.id as string,
		requestDbId as string,
	);

	const preparedDisableIntervals = useMemo(() => {
		return disabledDatesRanges
			? disabledDatesRanges.filter(interval => interval.from < interval.to).map((interval: any) => {
				return {
					start: getDateWithoutOffset(interval?.from),
					end: getDateWithoutOffset(interval?.to),
				};
			})
			: [];
	}, [disabledDatesRanges]);

	const options = useMemo(() => {
		return generateSelectOptions({
			subTextProp: 'ownerName',
			data: assetsList,
			value: 'id',
			label: ['assetId'],
		});
	}, [assetsList]);

	const setFromDate = useCallback((date: number) => {
		if (date) clearErrors('from');

		const toDate = getValues('to');

		if (date && toDate) {
			const isIntervalsIsInsideChosenDates = preparedDisableIntervals?.some(({ start, end }) =>
				date < start.getTime() && toDate > end.getTime());

			if (isIntervalsIsInsideChosenDates) {
				setError('from', { message: intervalErrorText });

				return;
			}
		}

		setValue('from', date, { shouldDirty: true });
	}, [clearErrors, getValues, preparedDisableIntervals, setError, setValue]);

	const setToDate = useCallback((date: number) => {
		if (date) clearErrors('to');

		const fromDate = getValues('from');

		if (date && fromDate) {
			const isIntervalsIsInsideChosenDates = preparedDisableIntervals?.some(({ start, end }) =>
				fromDate < start.getTime() && date > end.getTime());

			if (isIntervalsIsInsideChosenDates) {
				setError('to', { message: intervalErrorText });

				return;
			}
		}

		setValue('to', date, { shouldDirty: true });
	}, [clearErrors, getValues, preparedDisableIntervals, setError, setValue]);

	const handleCancel = useCallback(() => {
		closeModal();
	}, [closeModal]);

	const handleChangeAssetId = useCallback((value: TSelectOption) => {
		setValue('assetDbId', value, { shouldValidate: true });
	}, [setValue]);

	const closeOverlapDates = () => setIsOverlapDates(false);

	const loadMoreAssets = () => {
		setPageSize(pageSize * (page + 1));
		setPage(page + 1);
	};

	return (
		<>
			{(isLoadingAssets || isLoadingDates) && <SpinnerOverlay />}
			{selectedAssetId?.label
				? <div
					className={styles.assetId}
					data-testid='asset-id-label'
				>
					<span>Asset ID: </span>
					<span>{selectedAssetId?.label}</span>
				</div>
				: <Select
					control={control}
					disabled={!!selectedAssetId}
					errorMessage={errors?.assetDbId?.message}
					handleChange={handleChangeAssetId}
					isClearable
					isError={!!errors?.assetDbId?.message}
					isSearchable
					label='Asset ID'
					name='assetDbId'
					onMenuScrollToBottomCallback={loadMoreAssets}
					options={options}
					placeholder={assetIdPlaceholder}
					required
					value={selectedAssetId}
				/>
			}
			<div className={styles.datePickerContainer}>
				<DatepickerRange
					disableInput
					disabled={isLoadingDates}
					errorFromDate={errors?.from?.message as string}
					errorToDate={errors?.to?.message as string}
					excludeDateIntervalsFrom={preparedDisableIntervals}
					excludeDateIntervalsTo={preparedDisableIntervals}
					groupLabel='Rental Period'
					handleChangeFromDate={setFromDate}
					handleChangeToDate={setToDate}
					isMobileDevice={false}
					isTodayButtonFrom={false}
					isTodayButtonTo={false}
					minDateFrom={startOfTomorrow()}
					minDateTo={startOfTomorrow()}
					requiredFromDate
					requiredToDate
					value={{
						from: watch('from') as number | null,
						to: watch('to') as number | null,
					}}
				/>
			</div>
			<TextArea
				className={styles.comment}
				errorMessage={errors?.comment?.message}
				label='Comments'
				name='comment'
				placeholder='Leave comment'
				register={register}
				required={false}
				rows={5}
				withUseForms
			/>
			<div className={styles.buttonsContainer}>
				<Button
					className={styles.cancelBtn}
					onClick={handleCancel}
					text={cancelBtnText}
					withoutBackground
				/>
				<Button
					disabled={!isEmpty(errors)}
					onClick={handleSubmit(onSubmit)}
					text={confirmBtnText}
				/>
			</div>
			<Modal
				isOpen={isOverlapDates}
				onClose={closeOverlapDates}
				title='Invalid interval'
				width='350px'
			>
				<Inform
					informText='Sorry, the selected range is already taken. Please try another dates.'
					onClose={closeOverlapDates}
				/>
			</Modal>

		</>
	);
}

export default memo(AddNewAssetToRequest);
